/*
 * FRENESI — Cartão e vitrines do novo visual (19/10/2026).
 * snippets/fv-cartao e sections/fv-vitrine.
 *
 * Tamanho: cada botão traz a variante (data-v), o preço pago em centavos (data-p)
 * e o «de» (data-d, 0 sem desconto). Trocar o tamanho troca o Pix, o cartão, o 6x,
 * o «de … por», o selo de desconto, os links e a variante do formulário do tema —
 * quem põe na sacola é o próprio formulário (product-form-component).
 * Pix (10%) e 6x: as contas de assets/frenesi-payment-info.js, arredondadas no centavo.
 *
 * Vitrine: as setas andam uma tela de cartões e apagam nas pontas; os pontos
 * embaixo (um por posição do carrossel) acompanham a rolagem; o contador vai
 * até 23:59:59 no horário de Brasília, e a barra mostra quanto falta do dia.
 */
(() => {
  if (window.__fvCartao) return;
  window.__fvCartao = true;

  const real = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const brl = (centavos) => real.format(centavos / 100).replace(/ /g, ' ');
  const calmo = window.matchMedia('(prefers-reduced-motion: reduce)');

  const trocar = (el, texto) => {
    if (!el || el.textContent === texto) return;
    el.textContent = texto;
    if (calmo.matches) return;
    el.classList.remove('fv-troca');
    void el.offsetWidth;
    el.classList.add('fv-troca');
  };

  const comVariante = (url, v) => `${url}${url.includes('?') ? '&' : '?'}variant=${v}`;

  function escolher(botao) {
    const cartao = botao.closest('[data-fv-cartao]');
    if (!cartao || botao.disabled) return;
    const botoes = [...cartao.querySelectorAll('[data-fv-tam]')];
    for (const b of botoes) b.setAttribute('aria-pressed', String(b === botao));
    const sel = cartao.querySelector('[data-fv-sel]');
    if (sel) sel.value = String(botoes.indexOf(botao));

    const p = Number(botao.dataset.p);
    const d = Number(botao.dataset.d);
    const pix = Number(cartao.dataset.pix) || 0;
    const n = Number(cartao.dataset.parc) || 6;

    trocar(cartao.querySelector('[data-fv-pix]'), brl(Math.round((p * (100 - pix)) / 100)));
    trocar(cartao.querySelector('[data-fv-preco]'), brl(p));
    trocar(cartao.querySelector('[data-fv-parc]'), `em até ${n}x de ${brl(Math.round(p / n))} sem juros`);

    const linhaDe = cartao.querySelector('[data-fv-de-linha]');
    if (linhaDe) linhaDe.hidden = !(d > p);
    if (d > p) trocar(cartao.querySelector('[data-fv-de]'), brl(d));
    const off = cartao.querySelector('[data-fv-off]');
    if (off) {
      off.hidden = !(d > p);
      if (d > p) off.textContent = `${100 - Math.floor((p * 100) / d)}% OFF`;
    }

    for (const a of cartao.querySelectorAll('[data-fv-link]')) a.href = comVariante(cartao.dataset.url, botao.dataset.v);
    const campo = cartao.querySelector('[data-fv-variante]');
    if (campo) campo.value = botao.dataset.v;
    const mais = cartao.querySelector('.fv-cartao__mais');
    if (mais) mais.disabled = false;
  }

  /* ── Carrossel ─────────────────────────────────────────────────────────── */

  function marcar(vitrine) {
    const trilho = vitrine.querySelector('[data-fv-trilho]');
    if (!trilho) return;
    const fim = trilho.scrollWidth - trilho.clientWidth;
    for (const s of vitrine.querySelectorAll('[data-fv-seta]')) {
      s.disabled = Number(s.dataset.fvSeta) < 0 ? trilho.scrollLeft <= 4 : trilho.scrollLeft >= fim - 4;
    }
    pontos(vitrine, trilho, fim);
  }

  /* Um ponto por posição: o primeiro cartão visível de cada parada. */
  function pontos(vitrine, trilho, fim) {
    const caixa = vitrine.querySelector('[data-fv-pontos]');
    const c = trilho.firstElementChild;
    if (!caixa || !c) return;
    const gap = parseFloat(getComputedStyle(trilho).columnGap) || 0;
    const passo1 = c.getBoundingClientRect().width + gap;
    const n = fim > 4 && passo1 > 0 ? Math.round(fim / passo1) + 1 : 0;
    if (caixa.childElementCount !== n) caixa.innerHTML = '<span></span>'.repeat(n);
    const atual = Math.min(n - 1, Math.round(trilho.scrollLeft / passo1));
    [...caixa.children].forEach((p, i) => p.classList.toggle('is-ativo', i === atual));
    caixa.hidden = n < 2;
  }

  /* Uma tela por clique. A faixa de marcas vai de ponta a ponta da página e
     some nas bordas: ali a tela útil é a largura menos o respiro (scroll-padding),
     para nenhuma marca passar escondida no esmaecido. */
  function passo(trilho) {
    const c = trilho.firstElementChild;
    if (!c) return trilho.clientWidth;
    const estilo = getComputedStyle(trilho);
    const gap = parseFloat(estilo.columnGap) || 0;
    const respiro = (parseFloat(estilo.scrollPaddingLeft) || 0) + (parseFloat(estilo.scrollPaddingRight) || 0);
    const largura = c.getBoundingClientRect().width + gap;
    return largura * Math.max(1, Math.floor((trilho.clientWidth - respiro + gap) / largura));
  }

  /* ── Contador até a meia-noite (Brasília) ──────────────────────────────── */

  const fuso = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dois = (n) => String(n).padStart(2, '0');
  function bater() {
    const partes = fuso.formatToParts(new Date());
    const val = (t) => Number(partes.find((x) => x.type === t).value);
    const resta = Math.max(0, 86400 - ((val('hour') % 24) * 3600 + val('minute') * 60 + val('second')) - 1);
    const h = dois(Math.floor(resta / 3600));
    const m = dois(Math.floor((resta % 3600) / 60));
    const s = dois(resta % 60);
    for (const r of document.querySelectorAll('[data-fv-relogio]')) {
      r.querySelector('[data-h]').textContent = h;
      r.querySelector('[data-m]').textContent = m;
      r.querySelector('[data-s]').textContent = s;
      const barra = r.querySelector('[data-fv-relogio-barra]');
      if (barra) barra.style.width = `${((resta + 1) / 864).toFixed(2)}%`;
      r.setAttribute('aria-label', `Termina em ${h} horas, ${m} minutos e ${s} segundos`);
    }
  }

  /* ── Eventos ───────────────────────────────────────────────────────────── */

  document.addEventListener('click', (e) => {
    const alvo = e.target instanceof Element ? e.target : null;
    if (!alvo) return;
    const tam = alvo.closest('[data-fv-tam]');
    if (tam) {
      escolher(tam);
      return;
    }
    const seta = alvo.closest('[data-fv-seta]');
    if (seta) {
      const trilho = seta.closest('[data-fv-vitrine]')?.querySelector('[data-fv-trilho]');
      trilho?.scrollBy({ left: Number(seta.dataset.fvSeta) * passo(trilho), behavior: calmo.matches ? 'auto' : 'smooth' });
    }
  });

  /* Avise-me (20/10/2026): no cartão esgotado, abre ali mesmo a caixa do ERP
     (assets/frenesi-avise.js), já no tamanho escolhido — no lugar do app STOQ.
     Captura e para a propagação: o cartão inteiro é um link. Sem a caixa
     carregada, ou se a leitura do produto falhar, o link segue para a página
     do produto. */
  document.addEventListener('click', (e) => {
    const avise = e.target instanceof Element ? e.target.closest('[data-fv-avise]') : null;
    const caixa = window.FrenesiAvise;
    if (!avise || !caixa || typeof caixa.abrir !== 'function' || typeof HTMLDialogElement === 'undefined') return;
    e.preventDefault();
    e.stopPropagation();
    const cartao = avise.closest('[data-fv-cartao]');
    const escolhido = cartao?.querySelector('[data-fv-tam][aria-pressed="true"]') || cartao?.querySelector('[data-fv-tam]');
    caixa
      .abrir({ url: avise.getAttribute('href'), variante: escolhido?.dataset.v || avise.dataset.variante, origem: 'cartao' })
      .catch(() => {
        window.location.href = avise.href;
      });
  }, true);

  /* O seletor compacto (grade da coleção no celular) escolhe o mesmo botão. */
  document.addEventListener('change', (e) => {
    const sel = e.target instanceof Element ? e.target.closest('[data-fv-sel]') : null;
    if (!sel) return;
    const botao = sel.closest('[data-fv-cartao]')?.querySelectorAll('[data-fv-tam]')[Number(sel.value)];
    if (botao) escolher(botao);
  });

  const quadros = new WeakMap();
  function iniciar() {
    for (const v of document.querySelectorAll('[data-fv-vitrine]')) {
      if (v.dataset.fvPronto) continue;
      v.dataset.fvPronto = '1';
      const trilho = v.querySelector('[data-fv-trilho]');
      if (!trilho) continue;
      trilho.addEventListener('scroll', () => {
        if (quadros.get(v)) return;
        quadros.set(v, requestAnimationFrame(() => { quadros.set(v, 0); marcar(v); }));
      }, { passive: true });
      marcar(v);
    }
    if (document.querySelector('[data-fv-relogio]') && !window.__fvRelogio) {
      window.__fvRelogio = setInterval(bater, 1000);
      bater();
    }
  }

  window.addEventListener('resize', () => document.querySelectorAll('[data-fv-vitrine]').forEach(marcar), { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar, { once: true });
  else iniciar();
  document.addEventListener('shopify:section:load', iniciar);
})();
